import { useCallback, useEffect, useRef, useState } from 'react';

import type { SoftphoneClient, SoftphoneStatus } from './softphoneClient.types';

export interface UseSoftphoneParams {
  /** Provider-specific client, created by the surface (dashboard / extension) so the mic strategy is injected there. */
  client: SoftphoneClient;
  /** Mint a fresh browser token (tRPC `voiceCall.getClientToken`). */
  fetchToken: () => Promise<{ token: string }>;
  /** Create the server-side pending call and resolve the number to dial (tRPC `voiceCall.startCall`). */
  startServerCall: () => Promise<{
    voiceCallId: string;
    destinationNumber: string;
    callerNumber: string | null;
    recordingEnabled?: boolean;
  }>;
}

export interface SoftphoneController {
  status: SoftphoneStatus;
  durationSec: number;
  isMuted: boolean;
  error: string | null;
  isRecording: boolean;
  voiceCallId: string | null;
  start: () => Promise<void>;
  hangup: () => void;
  toggleMute: () => void;
}

/** Owns a SoftphoneClient's lifecycle and exposes call state to a presentational component. */
export function useSoftphone({ client, fetchToken, startServerCall }: UseSoftphoneParams): SoftphoneController {
  const [status, setStatus] = useState<SoftphoneStatus>('idle');
  const [durationSec, setDurationSec] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [voiceCallId, setVoiceCallId] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    client.on({
      onStatus: (s) => setStatus(s),
      onError: (message) => {
        setError(message);
        setStatus('error');
      },
    });
    return () => {
      void client.destroy();
    };
  }, [client]);

  // Tick the call timer only while the call is connected.
  useEffect(() => {
    if (status === 'active' && !timerRef.current) {
      timerRef.current = setInterval(() => setDurationSec((d) => d + 1), 1000);
    }
    if (status !== 'active' && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [status]);

  const start = useCallback(async () => {
    setError(null);
    setDurationSec(0);
    setIsMuted(false);
    setIsRecording(false);
    setStatus('connecting');
    try {
      const { token } = await fetchToken();
      await client.init(token);
      const server = await startServerCall();
      setVoiceCallId(server.voiceCallId);
      setIsRecording(server.recordingEnabled ?? false);
      await client.dial({
        destinationNumber: server.destinationNumber,
        callerNumber: server.callerNumber,
        voiceCallId: server.voiceCallId,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start call');
      setStatus('error');
    }
  }, [client, fetchToken, startServerCall]);

  const hangup = useCallback(() => {
    client.hangup();
  }, [client]);

  const toggleMute = useCallback(() => {
    setIsMuted((m) => {
      const next = !m;
      client.setMuted(next);
      return next;
    });
  }, [client]);

  return { status, durationSec, isMuted, error, isRecording, voiceCallId, start, hangup, toggleMute };
}
